import {
  IndicatorBadge,
  InlineFormattedNumber,
  toBigWithDefault,
  useCurrency,
  useSecurity,
  type CardProps,
  type Order,
  type OrderAnalytic,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { IWouldSizeChart } from './IWouldSizeChart';
import { DataCell, GridContainer, HeaderCell } from './styles';

export function IWouldCardContent({
  latestAnalytics,
  order,
  leg: propsLeg,
}: {
  order: Order;
  latestAnalytics: Record<number, OrderAnalytic>;
  leg?: number;
} & CardProps) {
  const isMultileg = order.LegSummary && propsLeg !== undefined;
  // Analytics for non-multileg are accessed by indexing leg 1.
  // And legs are displayed 1 and 2, but indexed 0 and 1
  // Important to note that IWould does not actually support multilegs at the moment, but this is future-proofing
  const leg = isMultileg ? propsLeg - 1 : 1;
  const orderAnalytics = latestAnalytics[propsLeg ?? 1];
  const currentLegSummary = useMemo(() => {
    return order.LegSummary?.find(legSummary => legSummary.LegIndex === leg);
  }, [leg, order.LegSummary]);
  const activeSymbol = isMultileg ? currentLegSummary?.Symbol : order.Symbol;
  const activeSecurity = useSecurity(activeSymbol);

  const qtyCurrency = useCurrency(isMultileg ? currentLegSummary?.Currency : order?.Currency);
  const priceCurrency = useCurrency(activeSecurity?.QuoteCurrency);

  if (!orderAnalytics) {
    return null;
  }

  const referenceSide = order.Parameters?.IWouldRefSide ?? 'None';
  const hasIWouldFills = !toBigWithDefault(orderAnalytics.IWouldFilledQty, 0).eq(0);
  return (
    <>
      <GridContainer isMultileg={isMultileg}>
        {isMultileg && <HeaderCell>Leg</HeaderCell>}
        <HeaderCell>Reference Side</HeaderCell>
        <HeaderCell align="right">Qty</HeaderCell>
        <HeaderCell align="right">Avg Px</HeaderCell>
        <HeaderCell align="right">Improvement</HeaderCell>

        {isMultileg && (
          <DataCell>
            <IndicatorBadge children={leg + 1} />
          </DataCell>
        )}
        <DataCell data-testid="reference-side">{referenceSide}</DataCell>
        <DataCell align="right" data-testid="iwould-filled-qty">
          <InlineFormattedNumber
            number={orderAnalytics.IWouldFilledQty}
            increment={qtyCurrency?.DefaultIncrement}
            currency={qtyCurrency?.Symbol}
          />
        </DataCell>
        <DataCell align="right" data-testid="iwould-avg-price">
          {!hasIWouldFills ? (
            'N/A'
          ) : (
            <InlineFormattedNumber
              number={orderAnalytics.IWouldAvgPrice}
              increment={priceCurrency?.DefaultIncrement}
              currency={priceCurrency?.Symbol}
            />
          )}
        </DataCell>
        <DataCell align="right" data-testid="iwould-price-improvement-bps">
          {!hasIWouldFills ? (
            'N/A'
          ) : (
            <InlineFormattedNumber number={orderAnalytics.IWouldPriceImprovementBps} increment="0.01" currency="BPS" />
          )}
        </DataCell>
      </GridContainer>
      <IWouldSizeChart
        cumQty={orderAnalytics.CumQty}
        iWouldQty={orderAnalytics.IWouldFilledQty}
        crossingQty={orderAnalytics.CrossingFilledQty}
      />
    </>
  );
}
